import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Stack,
  Paper,
} from '@mui/material';
import {
  Mail,
  Phone,
  Users,
  Video,
  FileText,
  X,
  Check,
  LucideIcon,
} from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import { NoteStructure } from '../../../../../utils/types/JobOpenings';

export interface AddNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: NoteStructure) => void;
  candidateName: string;
}

type ConversationType = 'Email' | 'Phone' | 'Person' | 'Video' | 'Other';

interface ConversationOption {
  value: ConversationType;
  label: string;
  icon: LucideIcon;
  color: string;
}

const CONVERSATION_TYPES: ConversationOption[] = [
  { value: 'Email', label: 'Email', icon: Mail, color: '#2563eb' },
  { value: 'Phone', label: 'Phone Call', icon: Phone, color: '#16a34a' },
  { value: 'Person', label: 'In-Person', icon: Users, color: '#9333ea' },
  { value: 'Video', label: 'Video Call', icon: Video, color: '#4f46e5' },
  { value: 'Other', label: 'Other', icon: FileText, color: '#64748b' },
];

export const AddNoteDialog = ({
  isOpen,
  onClose,
  onSave,
  candidateName,
}: AddNoteDialogProps) => {
  const [formData, setFormData] = useState<NoteStructure>({
    noteType: 'Email',
    noteSubject: '',
    noteBody: '',
    createdAt: new Date().toISOString().slice(0, 16),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.noteSubject.trim() || !formData.noteBody.trim()) {
      toast.error('Please fill in all required fields', {
        position: 'bottom-center',
      });
      return;
    }

    onSave({ ...formData });

    // Reset form
    setFormData({
      noteType: 'Email',
      noteSubject: '',
      noteBody: '',
      createdAt: new Date().toISOString().slice(0, 16),
    });

    toast.success('Note added successfully', {
      position: 'bottom-center',
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          bgcolor: '#059669',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FileText size={18} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>
              Add Note
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '11.5px' }}>
              Document conversation with {candidateName}
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: '#ffffff',
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)', transform: 'rotate(90deg)' },
            transition: 'all 0.2s ease',
          }}
        >
          <X size={16} />
        </IconButton>
      </DialogTitle>

      {/* Form Content */}
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Conversation Type */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary', fontSize: '13px' }}>
              Conversation Type <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {CONVERSATION_TYPES.map((type) => {
                const isSelected = formData.noteType === type.value;
                const Icon = type.icon;
                return (
                  <Paper
                    key={type.value}
                    elevation={0}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        noteType: type.value as any,
                      }))
                    }
                    sx={{
                      flex: 1,
                      minWidth: 80,
                      p: 1.25,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: isSelected ? type.color : 'grey.200',
                      bgcolor: isSelected ? `${type.color}10` : 'grey.50',
                      color: isSelected ? type.color : 'text.secondary',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.75,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: type.color,
                        bgcolor: `${type.color}08`,
                      },
                    }}
                  >
                    <Icon size={18} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isSelected ? 700 : 500,
                        fontSize: '11px',
                        textAlign: 'center',
                      }}
                    >
                      {type.label}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          {/* Conversation Time */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: 'text.primary', fontSize: '13px' }}>
              Conversation Date & Time <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              type="datetime-local"
              id="createdAt"
              name="createdAt"
              value={formData.createdAt}
              onChange={handleChange}
              fullWidth
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                },
              }}
            />
          </Box>

          {/* Subject */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: 'text.primary', fontSize: '13px' }}>
              Subject <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              id="noteSubject"
              name="noteSubject"
              value={formData.noteSubject}
              onChange={handleChange}
              placeholder="e.g., Follow-up on interview availability"
              fullWidth
              size="small"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                },
              }}
            />
          </Box>

          {/* Notes */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, color: 'text.primary', fontSize: '13px' }}>
              Notes <span style={{ color: '#ef4444' }}>*</span>
            </Typography>
            <TextField
              id="noteBody"
              name="noteBody"
              value={formData.noteBody}
              onChange={handleChange}
              placeholder="Enter detailed notes about the conversation..."
              fullWidth
              multiline
              rows={4}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '13px',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                Be specific and professional
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                {formData.noteBody.length} characters
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        {/* Action Buttons */}
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            type="button"
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: 'text.secondary',
              borderColor: 'grey.300',
              px: 2.5,
              py: 0.75,
              '&:hover': { bgcolor: 'grey.50', borderColor: 'grey.400' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Check size={16} />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              bgcolor: '#059669',
              px: 3,
              py: 0.75,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#047857', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)' },
            }}
          >
            Save Note
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
